import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Analysis } from '../types';
import { i18n } from '../i18n';
import { isValidUserId, isValidAnalysisNumber } from '../services/userIdService';
import { getAnalysisDataByKey, isAnalysisExpired } from '../services/resultStorageService';
import { loadAnalysisByExternalId } from '../services/analysisLoaderService';
import { 
  initializeSnapshotHistory, 
  createSnapshot, 
  canUndo, 
  canRedo, 
  undo, 
  redo, 
  getCurrentSnapshot,
  getCurrentSnapshotForResult,
  isCurrentResultInHistory,
  clearSnapshotHistory,
  clearResultSnapshotHistory,
  getResultSnapshotHistory,
  saveSnapshotHistory,
  setCurrentResultId,
  loadSnapshotHistoryIntoCache,
  getSnapshotHistory
} from '../services/snapshotService';
import AnalysisResult from './AnalysisResult';
import { AlertCircle, Home, RefreshCw, Share2, Undo2, Redo2 } from 'lucide-react';
import Disclaimer from './Disclaimer';

interface ResultPageProps {
  userId: string;
  analysisNumber: number;
  onGoHome: () => void;
  onNewAnalysis: () => void;
  onAnalysisLoaded?: (analysis: Analysis) => void;
  onAnalysisUpdate?: (analysis: Analysis) => void;
  onUseImprovedTemplate?: (template: string, expert?: string, person?: string, customExpert?: string, customPerson?: string) => void;
  onRestartWithQuestion?: (question: string, expert?: string, person?: string, customExpert?: string, customPerson?: string) => void;
  onError?: (hasError: boolean) => void;
  originalAnalysis?: Analysis | null;
  // Пропсы для шеринга
  onShare?: (analysis?: Analysis) => Promise<string | null>;
  onExportPDF?: (analysis?: Analysis) => Promise<void>;
  isSharing?: boolean;
  isExportingPDF?: boolean;
  shareUrl?: string | null;
  hasBeenShared?: boolean;
  shareError?: string | null;
  // Пропс для нового вопроса
  onNewQuestion?: () => void;
  theme?: 'dark' | 'light';
  language?: 'ru' | 'en';
  // Пропс для shareId (для расшаренных страниц)
  shareId?: string;
}
 
type LoadingState = 'loading' | 'success' | 'error' | 'not_found' | 'expired' | 'wrong_user';

interface ErrorState {
  type: LoadingState;
  message: string;
  details?: string;
}

export default function ResultPage({
  userId,
  analysisNumber,
  onGoHome,
  onNewAnalysis,
  onAnalysisLoaded,
  onAnalysisUpdate,
  onUseImprovedTemplate,
  onRestartWithQuestion,
  onError,
  originalAnalysis,
  theme = 'light',
  language = 'ru',
  onShare,
  onExportPDF,
  isSharing,
  isExportingPDF,
  shareUrl,
  hasBeenShared,
  shareError,
  onNewQuestion,
  shareId,
}: ResultPageProps) {
  const { t } = useTranslation('analysis');
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [initialAnalysis, setInitialAnalysis] = useState<Analysis | null>(null); // Сохраняем исходный анализ
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [error, setError] = useState<ErrorState | null>(null);
  const [isWrongUser, setIsWrongUser] = useState(false);
  const [canUndoState, setCanUndoState] = useState(false);
  const [canRedoState, setCanRedoState] = useState(false);

  useEffect(() => {
    // Предотвращаем двойные запросы в React StrictMode
    const timeoutId = setTimeout(() => {
      loadAnalysis();
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [userId, analysisNumber]);

  // Обновляем состояние кнопок отката при изменении анализа
  useEffect(() => {
    if (analysis) {
      // Обновляем состояние сразу и с задержкой для надежности
      updateUndoRedoState();
      const timeoutId = setTimeout(() => {
        updateUndoRedoState();
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [analysis]);

  // Устанавливаем title страницы при загрузке анализа (с учётом языка)
  useEffect(() => {
    const siteTitle = i18n.t('common:siteTitle');
    if (analysis) {
      const analysisTitle = analysis.seo?.title || analysis.title || analysis.description || 'Результаты анализа';
      document.title = `${analysisTitle} | ${siteTitle}`;
    } else {
      document.title = siteTitle;
    }
  }, [analysis]);

  const loadAnalysis = async () => {
    try {
      setLoadingState('loading');
      setError(null);
      setIsWrongUser(false);
      setAnalysis(null); // Сбрасываем предыдущий анализ

      // Проверяем валидность параметров
      if (!isValidUserId(userId)) {
        setError({
          type: 'error',
          message: 'Неверный формат идентификатора пользователя',
          details: 'ID пользователя должен состоять из 10 символов (буквы и цифры)'
        });
        setLoadingState('error');
        if (onError) {
          onError(true);
        }
        return;
      }

      if (!isValidAnalysisNumber(analysisNumber.toString())) {
        setError({
          type: 'error',
          message: 'Неверный номер анализа',
          details: 'Номер анализа должен быть положительным числом'
        });
        setLoadingState('error');
        if (onError) {
          onError(true);
        }
        return;
      }

      // Проверяем, является ли пользователь владельцем анализа
      // Сначала проверяем, есть ли ID в localStorage, не создавая новый
      const storedUserId = localStorage.getItem('plinus_user_id');
      if (storedUserId && userId !== storedUserId) {
        setIsWrongUser(true);
        setError({
          type: 'wrong_user',
          message: 'Это не ваш анализ',
          details: 'Автор ссылки отправил её по ошибке. Попросите его воспользоваться кнопкой "Поделиться" для корректного распространения.'
        });
        setLoadingState('wrong_user');
        if (onError) {
          onError(true);
        }
        return;
      }
      
      // Если у пользователя нет сохраненного ID, но он пытается открыть чужой анализ
      if (!storedUserId) {
        setIsWrongUser(true);
        setError({
          type: 'wrong_user',
          message: 'Это не ваш анализ',
          details: 'Автор ссылки отправил её по ошибке. Попросите его воспользоваться кнопкой "Поделиться" для корректного распространения.'
        });
        setLoadingState('wrong_user');
        if (onError) {
          onError(true);
        }
        return;
      }

      // Получаем данные анализа из localStorage
      const storedData = getAnalysisDataByKey(userId, analysisNumber);
      
      if (!storedData) {
        setError({
          type: 'not_found',
          message: 'Анализ не найден',
          details: 'Анализы хранятся 1 день. Возможно, анализ был удален по истечении срока хранения или ссылка неверна'
        });
        setLoadingState('not_found');
        if (onError) {
          onError(true);
        }
        return;
      }

      // Проверяем, не устарел ли анализ
      if (isAnalysisExpired(storedData.created_at)) {
        setError({
          type: 'expired',
          message: 'Анализ устарел',
          details: 'Анализы хранятся 1 день. Анализ был автоматически удален по истечении срока хранения'
        });
        setLoadingState('expired');
        if (onError) {
          onError(true);
        }
        return;
      }

      // Загружаем анализ с сервера
      const loadedAnalysis = await loadAnalysisByExternalId(storedData.external_id);
      const loadedExternalId = loadedAnalysis.external_id;
      
      // Создаем уникальный ID для результата
      const resultId = `${userId}_${analysisNumber}`;
      // Обновляем текущий идентификатор результата для snapshotService,
      // чтобы новые снимки корректно сохранялись даже при восстановлении истории
      setCurrentResultId(resultId);
      
      // Проверяем, есть ли снимки для этого результата
      const hasHistory = await isCurrentResultInHistory(resultId);
      
      if (hasHistory) {
        const isSnapshotFromAnotherResult = (candidate: Analysis | null) =>
          Boolean(
            candidate?.external_id &&
            loadedExternalId &&
            candidate.external_id !== loadedExternalId
          );

        // Загружаем историю в кэш
        await loadSnapshotHistoryIntoCache(resultId);
        
        // Восстанавливаем последний снимок
        let snapshot = getCurrentSnapshot();

        if (isSnapshotFromAnotherResult(snapshot)) {
          snapshot = null;
          clearSnapshotHistory();
          await clearResultSnapshotHistory(resultId);
        }

        // Если нет текущего снимка, пытаемся получить из сохраненной истории
        if (!snapshot) {
          const savedSnapshot = await getCurrentSnapshotForResult(resultId);
          if (savedSnapshot) {
            if (isSnapshotFromAnotherResult(savedSnapshot)) {
              await clearResultSnapshotHistory(resultId);
            } else {
              snapshot = savedSnapshot;
              const savedHistory = await getResultSnapshotHistory(resultId);
              if (savedHistory) {
                saveSnapshotHistory(savedHistory);
              }
            }
          }
        }

        if (snapshot) {
          // Восстанавливаем originalDecision из загруженного анализа, если его нет в snapshot
          // Это важно для старых snapshot, созданных до добавления originalDecision
          if (!snapshot.originalDecision) {
            if (loadedAnalysis.originalDecision) {
              // Используем originalDecision из загруженного анализа с сервера (исходные данные)
              snapshot.originalDecision = loadedAnalysis.originalDecision;
              console.log('📋 ResultPage - Восстановлен originalDecision из загруженного анализа:', snapshot.originalDecision);
            } else {
              // Fallback: вычисляем на основе исходных items (которые не были изменены)
              const { calculateVerdict } = await import('../utils/verdictCalculator');
              const originalItems = snapshot.items.filter(item => !item.isModified);
              if (originalItems.length > 0) {
                snapshot.originalDecision = calculateVerdict(originalItems);
                console.log('📋 ResultPage - Вычислен originalDecision на основе неизмененных items:', snapshot.originalDecision);
              } else {
                // Если все items изменены, вычисляем на основе всех items (fallback)
                snapshot.originalDecision = calculateVerdict(snapshot.items);
                console.log('📋 ResultPage - Вычислен originalDecision на основе всех items (fallback):', snapshot.originalDecision);
              }
            }
          } else {
            console.log('📋 ResultPage - originalDecision уже присутствует в snapshot:', snapshot.originalDecision);
          }
          
          // Восстанавливаем новые поля из загруженного анализа, если их нет в snapshot
          // Это важно для старых snapshot, созданных до добавления этих полей
          if (!snapshot.analysisMode && loadedAnalysis.analysisMode) {
            snapshot.analysisMode = loadedAnalysis.analysisMode;
          }
          if (!snapshot.argumentSelectionLogic && loadedAnalysis.argumentSelectionLogic) {
            snapshot.argumentSelectionLogic = loadedAnalysis.argumentSelectionLogic;
          }
          if (!snapshot.plusListTitle && loadedAnalysis.plusListTitle) {
            snapshot.plusListTitle = loadedAnalysis.plusListTitle;
          }
          if (!snapshot.minusListTitle && loadedAnalysis.minusListTitle) {
            snapshot.minusListTitle = loadedAnalysis.minusListTitle;
          }
          // Восстанавливаем SEO данные из загруженного анализа, если их нет в snapshot
          if (!snapshot.seo && loadedAnalysis.seo) {
            snapshot.seo = loadedAnalysis.seo;
          }
          
          setAnalysis(snapshot);
          setLoadingState('success');
          
          // Уведомляем родительский компонент о восстановленном анализе
          if (onAnalysisLoaded) {
            onAnalysisLoaded(snapshot);
          }
          
        } else {
          // Если снимка нет, используем загруженный анализ
          setAnalysis(loadedAnalysis);
          setInitialAnalysis(JSON.parse(JSON.stringify(loadedAnalysis))); // Сохраняем копию исходного анализа
          setLoadingState('success');
          
          // Инициализируем историю снимков
          await initializeSnapshotHistory(resultId, loadedAnalysis);
          
          // Уведомляем родительский компонент о загруженном анализе
          if (onAnalysisLoaded) {
            onAnalysisLoaded(loadedAnalysis);
          }
        }
      } else {
        // Очищаем старую историю и создаем новую
        clearSnapshotHistory();
        // Убеждаемся, что originalDecision присутствует
        if (!loadedAnalysis.originalDecision) {
          // Вычисляем originalDecision на основе исходных items (которые не были изменены)
          const { calculateVerdict } = await import('../utils/verdictCalculator');
          const originalItems = loadedAnalysis.items.filter(item => !item.isModified);
          if (originalItems.length > 0) {
            loadedAnalysis.originalDecision = calculateVerdict(originalItems);
          } else {
            // Если все items изменены, вычисляем на основе всех items (fallback)
            loadedAnalysis.originalDecision = calculateVerdict(loadedAnalysis.items);
          }
        }
        setAnalysis(loadedAnalysis);
        setInitialAnalysis(JSON.parse(JSON.stringify(loadedAnalysis))); // Сохраняем копию исходного анализа
        setLoadingState('success');
        
        // Инициализируем историю снимков
        await initializeSnapshotHistory(resultId, loadedAnalysis);
        
        // Уведомляем родительский компонент о загруженном анализе
        if (onAnalysisLoaded) {
          onAnalysisLoaded(loadedAnalysis);
        }
      }
      
      // Обновляем состояние кнопок отката
      updateUndoRedoState();
      
      // Уведомляем, что ошибки нет
      if (onError) {
        onError(false);
      }

    } catch (error) {
      console.error('Ошибка при загрузке анализа:', error);
      
      if (error instanceof Error && error.message === 'ANALYSIS_NOT_FOUND') {
        setError({
          type: 'not_found',
          message: 'Анализ не найден на сервере',
          details: 'Анализы хранятся 1 день. Анализ мог быть удален по истечении срока хранения'
        });
        setLoadingState('not_found');
      } else {
        setError({
          type: 'error',
          message: 'Ошибка при загрузке анализа',
          details: error instanceof Error ? error.message : 'Неизвестная ошибка'
        });
        setLoadingState('error');
      }
      
      // Уведомляем, что произошла ошибка
      if (onError) {
        onError(true);
      }
    }
  };

  // Функция для обновления состояния кнопок отката
  const updateUndoRedoState = () => {
    setCanUndoState(canUndo());
    setCanRedoState(canRedo());
  };

  // Функция для создания снимка при изменении
  const createSnapshotForChange = (updatedAnalysis: Analysis, description: string) => {
    // Получаем resultId для передачи в createSnapshot
    const resultId = `${userId}_${analysisNumber}`;
    
    // Убеждаемся, что currentResultId установлен
    setCurrentResultId(resultId);
    
    // Если истории нет, инициализируем её с исходным анализом (если он есть)
    // Это важно для того, чтобы при первом изменении можно было откатить к исходному состоянию
    const history = getSnapshotHistory();
    if (!history || history.currentResultId !== resultId) {
      const baseAnalysis = initialAnalysis || analysis; // Используем исходный анализ, если он есть
      if (baseAnalysis) {
        initializeSnapshotHistory(resultId, baseAnalysis);
      }
    }
    
    const undoRedoState = createSnapshot(updatedAnalysis, description, resultId);
    
    // Если createSnapshot вернул состояние, используем его напрямую
    if (undoRedoState) {
      setCanUndoState(undoRedoState.canUndo);
      setCanRedoState(undoRedoState.canRedo);
    } else {
      // Если не удалось создать снимок, обновляем состояние
      updateUndoRedoState();
    }
  };

  // Обработчики отката и возврата
  const handleUndo = () => {
    const previousAnalysis = undo();
    if (previousAnalysis) {
      setAnalysis(previousAnalysis);
      // Обновляем состояние
      updateUndoRedoState();
      onAnalysisUpdate?.(previousAnalysis);
    }
  };

  const handleRedo = () => {
    const nextAnalysis = redo();
    if (nextAnalysis) {
      setAnalysis(nextAnalysis);
      // Обновляем состояние
      updateUndoRedoState();
      onAnalysisUpdate?.(nextAnalysis);
    }
  };

  const handleItemMove = (itemId: string, newCategory: 'plus' | 'minus') => {
    if (!analysis) return;
    
    const item = analysis.items.find(item => item.id === itemId);
    if (!item) return;
    
    const updatedItems = analysis.items.map(item =>
      item.id === itemId ? { ...item, category: newCategory, isModified: true } : item
    );
    
    const updatedAnalysis = {
      ...analysis,
      items: updatedItems,
      isModified: true,
      originalDecision: analysis.originalDecision // Сохраняем originalDecision при обновлении
    };
    
    setAnalysis(updatedAnalysis);
    
    // Создаем снимок с описанием изменения
    const description = `Перемещение аргумента "${item.text.substring(0, 30)}${item.text.length > 30 ? '...' : ''}" в ${newCategory === 'plus' ? 'плюсы' : 'минусы'}`;
    createSnapshotForChange(updatedAnalysis, description);
    
    // Передаем изменения в родительский компонент
    onAnalysisUpdate?.(updatedAnalysis);
  };

  const handleRatingChange = (itemId: string, rating: number) => {
    if (!analysis) return;
    
    const item = analysis.items.find(item => item.id === itemId);
    if (!item) return;
    
    // Находим исходную оценку из оригинального анализа
    const originalItem = originalAnalysis?.items.find(item => item.id === itemId);
    const originalRating = originalItem?.rating;
    
    // Определяем, изменился ли элемент
    const isModified = originalRating !== undefined && originalRating !== rating;
    
    const updatedItems = analysis.items.map(item =>
      item.id === itemId ? { ...item, rating, isModified } : item
    );
    
    const updatedAnalysis = {
      ...analysis,
      items: updatedItems,
      isModified: true,
      originalDecision: analysis.originalDecision // Сохраняем originalDecision при обновлении
    };
    
    setAnalysis(updatedAnalysis);
    
    // Создаем снимок с описанием изменения
    const description = `Изменение рейтинга аргумента "${item.text.substring(0, 30)}${item.text.length > 30 ? '...' : ''}" с ${item.rating} на ${rating}`;
    createSnapshotForChange(updatedAnalysis, description);
    
    // Передаем изменения в родительский компонент
    onAnalysisUpdate?.(updatedAnalysis);
  };

  const handleItemDelete = (itemId: string) => {
    if (!analysis) return;
    
    const item = analysis.items.find(item => item.id === itemId);
    if (!item) return;
    
    const updatedItems = analysis.items.filter(item => item.id !== itemId);
    
    const updatedAnalysis = {
      ...analysis,
      items: updatedItems,
      isModified: true,
      originalDecision: analysis.originalDecision // Сохраняем originalDecision при обновлении
    };
    
    setAnalysis(updatedAnalysis);
    
    // Создаем снимок с описанием изменения
    const description = `Удаление аргумента "${item.text.substring(0, 30)}${item.text.length > 30 ? '...' : ''}"`;
    createSnapshotForChange(updatedAnalysis, description);
    
    // Передаем изменения в родительский компонент
    onAnalysisUpdate?.(updatedAnalysis);
  };

  const handleAddItem = (text: string, category: 'plus' | 'minus', rating: number) => {
    if (!analysis) return;
    
    const newItem = {
      id: `custom_${Date.now()}`,
      text,
      rating,
      category,
      isModified: true
    };
    
    const updatedItems = [...analysis.items, newItem];
    
    const updatedAnalysis = {
      ...analysis,
      items: updatedItems,
      isModified: true,
      originalDecision: analysis.originalDecision // Сохраняем originalDecision при обновлении
    };
    
    setAnalysis(updatedAnalysis);
    
    // Создаем снимок с описанием изменения
    const description = `Добавление аргумента "${text.substring(0, 30)}${text.length > 30 ? '...' : ''}" в ${category === 'plus' ? 'плюсы' : 'минусы'}`;
    createSnapshotForChange(updatedAnalysis, description);
    
    // Передаем изменения в родительский компонент
    onAnalysisUpdate?.(updatedAnalysis);
  };

  const handleAnalysisUpdate = (updatedAnalysis: Analysis) => {
    setAnalysis(updatedAnalysis);
  };

  if (loadingState === 'loading') {
    const isDark = theme === 'dark';
    return (
      <div className={`rounded-lg shadow-sm p-8 text-center ${
        isDark 
          ? 'bg-slate-800/50 border border-slate-700' 
          : 'bg-white'
      }`}>
        <RefreshCw className={`w-8 h-8 animate-spin mx-auto mb-4 ${
          isDark ? 'text-blue-400' : 'text-blue-600'
        }`} />
        <h2 className={`text-xl font-semibold mb-2 ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>{t('loadingAnalysis')}</h2>
        <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>{t('pleaseWait')}</p>
      </div>
    );
  }

  if (error) {
    const isDark = theme === 'dark';
    return (
      <div className={`rounded-lg shadow-sm p-8 ${
        isDark 
          ? 'bg-slate-800/50 border border-slate-700' 
          : 'bg-white'
      }`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className={`text-2xl font-semibold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>{error.message}</h2>
          {error.details && (
            <p className={`mb-6 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>{error.details}</p>
          )}
          
          {isWrongUser && (
            <div className={`border rounded-lg p-4 mb-6 ${
              isDark
                ? 'bg-yellow-900/20 border-yellow-800/50'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center justify-center mb-2">
                <Share2 className={`w-5 h-5 mr-2 ${
                  isDark ? 'text-yellow-400' : 'text-yellow-600'
                }`} />
                <span className={`font-medium ${
                  isDark ? 'text-yellow-300' : 'text-yellow-800'
                }`}>Решения по распространению</span>
              </div>
              <p className={`text-sm ${
                isDark ? 'text-yellow-200' : 'text-yellow-700'
              }`}>
                Для корректного распространения анализа попросите автора воспользоваться кнопкой "Поделиться" в интерфейсе приложения.
              </p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onGoHome}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Home className="w-4 h-4 mr-2" />
              На главную
            </button>
            <button
              onClick={onNewAnalysis}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Новый анализ
            </button>
            {loadingState !== 'wrong_user' && loadingState !== 'not_found' && loadingState !== 'expired' && (
              <button
                onClick={loadAnalysis}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Попробовать снова
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (analysis) {
    return (
      <>
        {/* Плавающая панель управления откатом */}
        {(canUndoState || canRedoState) && (
          <div className="fixed bottom-4 left-2 sm:left-4 sm:bottom-auto sm:top-32 sm:right-6 sm:left-auto z-40 bg-white rounded-lg shadow-lg border border-gray-200 p-2 sm:p-3 animate-slide-in-right max-w-[calc(100vw-1rem)] sm:max-w-none">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <span className="text-xs font-medium text-gray-600 hidden sm:inline">Изменения:</span>
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleUndo}
                  disabled={!canUndoState}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    canUndoState 
                      ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 hover:scale-105' 
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title={canUndoState ? 'Отменить последнее изменение' : 'Нет изменений для отмены'}
                >
                  <Undo2 className="h-4 w-4" />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={!canRedoState}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    canRedoState 
                      ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 hover:scale-105' 
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title={canRedoState ? 'Вернуть отмененное изменение' : 'Нет изменений для возврата'}
                >
                  <Redo2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-4 pr-0 sm:pr-0">
          <AnalysisResult
            analysis={analysis}
            onItemMove={handleItemMove}
            onRatingChange={handleRatingChange}
            onItemDelete={handleItemDelete}
            onAddItem={handleAddItem}
            onAnalysisUpdate={handleAnalysisUpdate}
            isReadOnly={false}
            onUseImprovedTemplate={onUseImprovedTemplate}
            onRestartWithQuestion={onRestartWithQuestion}
            theme={theme}
            language={language}
            // Пропсы для шеринга
            onShare={onShare}
            onExportPDF={onExportPDF}
            isSharing={isSharing}
            isExportingPDF={isExportingPDF}
            shareUrl={shareUrl}
            hasBeenShared={hasBeenShared}
            shareError={shareError}
            // Пропс для нового вопроса
            onNewQuestion={onNewQuestion}
            // Пропс для shareId
            shareId={shareId}
          />
        </div>
        
        {/* Disclaimer */}
        <Disclaimer className="mt-8" theme={theme} />
      </>
    );
  }

  return null;
}
